import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  
  AffecterProjectBody,
  Commune,
  Prefecture,
  ProjectBody,
  ProjectDetailsModel,
  ProjectInterface,
  ProjectModel,
  ProjectNamesModel,
  Region,
} from "../../domaine/projectModel";
import {
  addProjectApi,
  addTrancheApi,
  affecterProjectApi,
  completeProjectApi,
  deleteProjectApi,
  deleteTrancheApi,
  editProjectApi,
  editTrancheApi,
  fetchCommunesApi,
  fetchPrefecturesApi,
  fetchProjectDetailsApi,
  fetchProjectsApi,
  fetchProjectsNamesApi,
  fetchRegionsApi,
  retirerProjectApi,
} from "../../api/projects.api";
import { persistProjectID } from "../../services/localStorage.Service";
import { fetchUsersWithProjects } from "./userSlice";
import toast from "react-hot-toast";

interface ProjectsState {
  projects: ProjectInterface[];
  projectNames: ProjectNamesModel[];
  projectDetails: ProjectDetailsModel;
  selectedProjects: any[];
  regions: Region[];
  prefectures: Prefecture[];
  communes: Commune[];
  loading: "idle" | "pending" | "succeeded" | "failed";
}

const initialState: ProjectsState = {
  projects: [],
  projectNames: [],
  projectDetails: {} as ProjectDetailsModel,
  selectedProjects: [],
  regions: [],
  prefectures: [],
  communes: [],
  loading: "idle",
};

const API_URL = "projets/";

export const fetchProjects: any = createAsyncThunk(
  "Projects/fetchProjects",
  async () => {
    const projects: ProjectInterface[] = await fetchProjectsApi(API_URL);
    return projects;
  }
);

export const fetchProjectsNames: any = createAsyncThunk(
  "Projects/fetchProjectsNames",
  async () => {
    const projectsNames: ProjectNamesModel[] = await fetchProjectsNamesApi(
      "projets/all/"
    );
    return projectsNames;
  }
);
export const deleteProject: any = createAsyncThunk(
  "Projects/deleteProject",
  async (ProjectId: number, { dispatch }) => {
    const project = deleteProjectApi(ProjectId, API_URL);
    dispatch(fetchProjects());
    return project;
  }
);

export const deleteTranche: any = createAsyncThunk(
  "Projects/deleteTranche",
  async (trancheId: number, { dispatch }) => {
    const tranche = deleteTrancheApi(trancheId, "tranches/");
    return tranche;
  }
);

export const fetchProjectDetails: any = createAsyncThunk(
  "Projects/fetchProjectDetails",
  async (ProjectId: number) => {
    const project = await fetchProjectDetailsApi(ProjectId, API_URL);
    persistProjectID(project.id);
    return project;
  }
);

export const fetchRegions: any = createAsyncThunk(
  "Projects/fetchRegions",
  async () => {
    const regions: Region[] = await fetchRegionsApi("regions/");
    return regions;
  }
);

export const fetchPrefectures: any = createAsyncThunk(
  "Projects/fetchPrefecures",
  async (regionId: number) => {
    const prefectures: Region[] = await fetchPrefecturesApi(
      regionId,
      "prefecures/"
    );
    return prefectures;
  }
);

export const fetchCommunes: any = createAsyncThunk(
  "Projects/fetchCommunes",
  async (prefectureId: number) => {
    const communes: Commune[] = await fetchCommunesApi(
      prefectureId,
      "communes/"
    );
    return communes;
  }
);

export const addProject: any = createAsyncThunk(
  "Projects/addProject",
  async (newProject: ProjectBody, { dispatch }) => {
    const project = await addProjectApi(newProject, API_URL);
    return project;
  }
);

export const editProject: any = createAsyncThunk(
  "Projects/editProject",
  async ({ newProject, id }: { newProject: any; id: number }, { dispatch }) => {
    console.log("id", id);
    const project = await editProjectApi(newProject, id, API_URL);
    return project;
  }
);

export const addTranche: any = createAsyncThunk(
  "Projects/addTranche",
  async (newTranche: any, { dispatch }) => {
    const tranche = await addTrancheApi(newTranche, "tranches/");
    return tranche;
  }
);

export const editTranche: any = createAsyncThunk(
  "Projects/editTranche",
  async ({ newTranche, id }: { newTranche: any; id: number }, { dispatch }) => {
    console.log("id", id);
    const tranche = await editTrancheApi(newTranche, id, "tranches/");
    await dispatch(fetchUsersWithProjects());
    return tranche;
  }
);

export const affecterProject: any = createAsyncThunk(
  "Projects/affecterProject",
  async (body: AffecterProjectBody, { dispatch }) => {
    const result = await affecterProjectApi(body, "visiteur-projet/");
    
    // If there were partially successful assignments, display a success message
    if (result.success) {
      toast.success(result.success);
    }
    
    // If there were errors with some projects, but others succeeded
    if (result.errors && result.errors.length > 0) {
      console.log("Some assignments failed:", result.errors);
    }
    
    return result;
  }
);



export const retirerProject: any = createAsyncThunk(
  "Projects/retirerProject",
  async ({ idProjet, idUser }: { idProjet: number; idUser: number }, { dispatch }) => {
    const result = await retirerProjectApi(idProjet, idUser, "visiteur-projet/");
    await dispatch(fetchUsersWithProjects());
    return result;
  }
);

export const completeProject: any = createAsyncThunk(
  "Projects/completeProject",
  async ({ idProjet, idUser }: { idProjet: number; idUser: number }, { dispatch }) => {
    const result = await completeProjectApi(idProjet, idUser, "visiteur-projet/");
    await dispatch(fetchUsersWithProjects());
    return result;
  }
);

const projectsSlice = createSlice({
  name: "projects",
  initialState,
  reducers: {
    // Add a logout action to reset state
    deleteProjectsData: (state) => {
      state.projects = [];
      state.projectNames = [];
      state.loading = "idle";
    },
    
      setSelectedProjects: (state, action) => {
        state.selectedProjects = action.payload;
      }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProjects.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.projects = action.payload;
      })
      .addCase(fetchProjects.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(addProject.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(addProject.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(addProject.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(fetchProjectsNames.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(editProject.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(editProject.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(editProject.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(addTranche.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(addTranche.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(addTranche.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(editTranche.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(editTranche.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(editTranche.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(deleteTranche.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(deleteTranche.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(deleteTranche.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(fetchProjectsNames.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.projectNames = action.payload;
      })
      .addCase(fetchProjectsNames.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(deleteProject.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(deleteProject.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(deleteProject.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(affecterProject.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(affecterProject.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(affecterProject.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(retirerProject.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(retirerProject.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(retirerProject.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(completeProject.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(completeProject.fulfilled, (state, action) => {
        state.loading = "succeeded";
      })
      .addCase(completeProject.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(fetchProjectDetails.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchProjectDetails.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.projectDetails = action.payload;
      })
      .addCase(fetchProjectDetails.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(fetchRegions.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchRegions.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.regions = action.payload;
      })
      .addCase(fetchRegions.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(fetchPrefectures.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchPrefectures.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.prefectures = action.payload;
      })
      .addCase(fetchPrefectures.rejected, (state) => {
        state.loading = "failed";
      })
      .addCase(fetchCommunes.pending, (state) => {
        state.loading = "pending";
      })
      .addCase(fetchCommunes.fulfilled, (state, action) => {
        state.loading = "succeeded";
        state.communes = action.payload;
      })
      .addCase(fetchCommunes.rejected, (state) => {
        state.loading = "failed";
      });
  },
});

export const { deleteProjectsData } = projectsSlice.actions;
export const { setSelectedProjects } = projectsSlice.actions;

export default projectsSlice.reducer;
